import type { DetectionSummaryDto } from '@signalvote/shared';
import { aliasCanonicalLabel } from '@signalvote/shared';

/**
 * ADR-015 D7 — the detection-summary banner shown above the row grid in the
 * import preview. It surfaces the omissions a row grid can't ("you can't
 * eyeball an absence"): how many rows imported, how many look like refunds,
 * how many were skipped (and why), whether this file was already imported, and
 * which columns were read under an alias.
 *
 * Tone is reassurance, never alarm (the product's no-silent-surprise
 * direction): every count is shown — including zeros, phrased positively — in a
 * muted baseline. The single soft-amber accent is reserved for `skipped > 0`
 * and the already-imported notice; red is never used here (red is for hard
 * errors). Refund-shaped rows are normal money movement and are never styled as
 * a warning.
 *
 * Standalone + reusable on purpose: the bank mapping preview can mount it later
 * (it does not in this slice), and the `caveat` slot is where the adapter
 * under-bind notice ("couldn't fully read this Numero file") drops in.
 */
interface DetectionSummaryBannerProps {
  summary: DetectionSummaryDto;
}

// The adapters' only structural row-DROP signal is a warning ending in this
// sentinel (the same sentinel SkippedRowCount counts server-side). Everything
// else is a DEGRADE/file-level note on a row that WAS imported — surfaced, but
// never mislabeled as a skip.
const SKIPPED_SENTINEL = '— skipped.';

export function DetectionSummaryBanner({ summary }: DetectionSummaryBannerProps) {
  const {
    importedRowCount,
    refundShapedRowCount,
    skippedRowCount,
    // lowConfidenceRowCount: deferred — the scanner slice (Tier 2) owns the
    // low-confidence UX; the adapter preview always reports 0.
    suspectedDuplicate,
    duplicateMessage,
    caveat,
    aliasResolutions,
    warnings,
  } = summary;

  const skipWarnings = warnings.filter((w) => w.trimEnd().endsWith(SKIPPED_SENTINEL));
  const degradeWarnings = warnings.filter((w) => !w.trimEnd().endsWith(SKIPPED_SENTINEL));

  const importedLabel = `${importedRowCount} ${
    importedRowCount === 1 ? 'row' : 'rows'
  } imported`;

  const refundLabel =
    refundShapedRowCount === 0
      ? 'No refunds'
      : `${refundShapedRowCount} ${
          refundShapedRowCount === 1 ? 'row looks' : 'rows look'
        } like refunds`;

  const skippedLabel =
    skippedRowCount === 0
      ? 'No rows skipped'
      : `${skippedRowCount} ${skippedRowCount === 1 ? 'row' : 'rows'} skipped`;

  return (
    // aria-live polite: a screen-reader hears the omission counts when the
    // summary resolves. No entrance animation by default (reduced-motion-safe).
    <section
      aria-live="polite"
      aria-label="What we found in your file"
      className="rounded-md border bg-card p-4 text-sm space-y-3"
    >
      <p className="font-medium text-foreground">Here&rsquo;s what we found</p>

      {/* Counts wrap (no fixed widths / no truncate) so they stay legible at
          font-scale 1.3 / 200% zoom. */}
      <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-muted-foreground">
        <span className="text-foreground">{importedLabel}</span>
        <span>{refundLabel}</span>
        <span className={skippedRowCount > 0 ? 'text-amber-700' : undefined}>
          {skippedLabel}
        </span>
      </div>

      {aliasResolutions.length > 0 && (
        <div className="space-y-1">
          <p className="text-muted-foreground">
            Some of your columns had different names — here&rsquo;s how we read
            them:
          </p>
          <ul className="space-y-0.5">
            {aliasResolutions.map((alias, i) => (
              <li key={`${alias.canonical}-${i}`} className="text-foreground">
                Read &ldquo;{alias.matchedHeader}&rdquo; as{' '}
                {aliasCanonicalLabel(alias.canonical)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipWarnings.length > 0 && (
        <div className="space-y-1">
          <p className="text-muted-foreground">Why some rows were skipped:</p>
          {/* Muted, not amber — the soft-amber accent stays on the skip COUNT
              and the duplicate notice (the single accent per the emphasis
              ladder); the reasons themselves read as plain detail. */}
          <ul className="space-y-0.5 text-muted-foreground">
            {skipWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {degradeWarnings.length > 0 && (
        <div className="space-y-1">
          {/* Imported-but-degraded rows (e.g. an unrecognized wallet mapped to
              Other). Shown regardless of the skip count — "you can't eyeball an
              absence" — and never styled as alarm. */}
          <p className="text-muted-foreground">A couple of things to double-check:</p>
          <ul className="space-y-0.5 text-muted-foreground">
            {degradeWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {suspectedDuplicate && duplicateMessage && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1 text-amber-800">
          {/* Server-built, campaign-local copy — rendered verbatim (the server
              owns the date logic). A heads-up only: it never disables import;
              the server still owns the hard duplicate block. */}
          <p>{duplicateMessage}</p>
        </div>
      )}

      {caveat && (
        // Reserved slot for the adapter under-bind caveat (a later slice).
        <p className="text-muted-foreground">{caveat}</p>
      )}
    </section>
  );
}
